import { motion } from "framer-motion";
import { ShoppingBag, Pizza, Globe, Store } from "lucide-react";
import piccover from "@/assets/images/Logo.png";

export default function SmartlyOrderPromo() {
  return (
    <div className="min-h-screen bg-linear-to-b from-green-50 to-white py-12 px-4 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <h2 className="text-green-700 font-bold text-sm tracking-wider uppercase mb-2">
            WHY USE
          </h2>
          <h1 className="text-5xl md:text-7xl font-black text-orange-500">
            SMARTLY ORDERS
          </h1>
        </motion.div>

        {/* Main Content */}
        <div className="relative flex items-center justify-center">
          {/* Left Benefits */}
          <motion.div
            className="hidden lg:flex flex-col gap-16 mr-8"
            initial={{ opacity: 0, x: -80 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <div className="text-right max-w-xs">
              <div className="flex justify-end mb-3">
                <motion.div
                  className="w-12 h-12 flex items-center justify-center"
                  whileHover={{ scale: 1.1, rotate: 10 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <ShoppingBag
                    className="w-10 h-10 text-emerald-500"
                    strokeWidth={1.5}
                  />
                </motion.div>
              </div>
              <h3 className="text-emerald-500 font-bold text-lg leading-tight">
                ORDER FOOD, GROCERIES
                <br />& SAVE MORE EVERY DAY
              </h3>
            </div>

            <div className="text-right max-w-xs">
              <div className="flex justify-end mb-3">
                <motion.div
                  className="w-12 h-12 flex items-center justify-center"
                  whileHover={{ scale: 1.1, rotate: -8 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <Globe
                    className="w-10 h-10 text-emerald-500"
                    strokeWidth={1.5}
                  />
                </motion.div>
              </div>
              <h3 className="text-emerald-500 font-bold text-lg leading-tight">
                SAVE MONEY WHILE <br />
                REDUCING FOOD WASTE
              </h3>
            </div>
          </motion.div>

          {/* Center - Bag Image */}
          <motion.div
            className="relative shrink-0 shadow-lg rounded-2xl overflow-hidden"
            initial={{ opacity: 0, y: 80 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <div className="w-80 h-96 md:w-96 md:h-125 bg-linear-to-br from-emerald-600 to-teal-700 rounded-t-3xl shadow-2xl relative overflow-hidden">
              {/* Texture overlay */}
              <div className="absolute inset-0 bg-linear-to-br from-transparent via-teal-500/20 to-transparent"></div>

              {/* Food items peeking */}
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-full flex justify-center gap-2 px-8">
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="w-16 h-20 bg-green-600 rounded-lg transform -rotate-12 shadow-lg"
                />
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="w-20 h-24 bg-orange-500 rounded-full shadow-lg"
                />
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="w-12 h-28 bg-green-700 rounded transform rotate-6 shadow-lg"
                />
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="w-14 h-20 bg-red-500 rounded-full transform -rotate-6 shadow-lg"
                />
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="w-10 h-24 bg-green-600 rounded transform rotate-12 shadow-lg"
                />
              </div>

              {/* Milk carton */}
              <motion.div
                className="absolute top-0 left-12 w-16 h-24 bg-white rounded-t-sm shadow-lg transform -rotate-6"
                whileHover={{ rotate: 0, y: -5 }}
              >
                <div className="w-full h-6 bg-blue-300 mt-2"></div>
              </motion.div>

              {/* Logo */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                <motion.div
                  className="mb-4"
                  whileHover={{ scale: 1.05, rotate: 2 }}
                >
                  <img
                    src={piccover}
                    alt="Smartly Order Logo"
                    className="w-32 h-24 mx-auto object-contain drop-shadow-lg"
                  />
                </motion.div>
                <motion.h2
                  className="text-white text-2xl font-bold tracking-wider"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  SMARTLY ORDERS
                </motion.h2>
              </div>
            </div>
          </motion.div>

          {/* Right Benefits */}
          <motion.div
            className="hidden lg:flex flex-col gap-16 ml-8"
            initial={{ opacity: 0, x: 80 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <div className="text-left max-w-xs">
              <div className="flex justify-start mb-3">
                <motion.div
                  className="w-12 h-12 flex items-center justify-center"
                  whileHover={{ scale: 1.1, rotate: -10 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <Pizza
                    className="w-10 h-10 text-emerald-500"
                    strokeWidth={1.5}
                  />
                </motion.div>
              </div>
              <h3 className="text-emerald-500 font-bold text-lg leading-tight">
                DISCOVER EXCLUSIVE <br />
                MYSTERY BAGS
              </h3>
            </div>

            <div className="text-left max-w-xs">
              <div className="flex justify-start mb-3">
                <motion.div
                  className="w-12 h-12 flex items-center justify-center"
                  whileHover={{ scale: 1.1, rotate: 10 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <Store
                    className="w-10 h-10 text-emerald-500"
                    strokeWidth={1.5}
                  />
                </motion.div>
              </div>
              <h3 className="text-emerald-500 font-bold text-lg leading-tight">
                ORDER FROM RESTAURANTS,<br />SUPERMARKETS & LOCAL STORES
              </h3>
            </div>
          </motion.div>
        </div>

        {/* Mobile Benefits */}
        <motion.div
          className="lg:hidden mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 80 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          {[
            {
              icon: (
                <ShoppingBag
                  className="w-10 h-10 text-green-700"
                  strokeWidth={1.5}
                />
              ),
              text: "ENJOY GOOD FOOD AT ½ PRICE OR LESS",
            },
            {
              icon: (
                <Pizza className="w-10 h-10 text-teal-700" strokeWidth={1.5} />
              ),
              text: "RESCUE FOOD NEAR YOU",
            },
            {
              icon: (
                <Globe className="w-10 h-10 text-teal-700" strokeWidth={1.5} />
              ),
              text: "HELP THE ENVIRONMENT BY REDUCING FOOD WASTE",
            },
            {
              icon: (
                <Store className="w-10 h-10 text-teal-700" strokeWidth={1.5} />
              ),
              text: "TRY SOMETHING NEW FROM LOCAL CAFES, BAKERIES OR RESTAURANTS",
            },
          ].map((item, index) => (
            <motion.div
              key={index}
              className="text-center"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 150 }}
            >
              <div className="flex justify-center mb-3">{item.icon}</div>
              <h3 className="text-teal-700 font-bold text-base">{item.text}</h3>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
